import React from 'react';
import styles from './StatTile.module.css';

export interface StatTileTrend {
  value: string;
  isUp: boolean;
}

export interface StatTileProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subtitle?: string;
  trend?: StatTileTrend | null;
  trendTone?: 'auto' | 'success' | 'warning' | 'danger' | 'neutral';
  spark?: number[];
  sparkColor?: string;
  loading?: boolean;
  title?: string;
}

function Sparkline({ data, color }: { data: number[]; color: string }) {
  if (data.length < 2) return null;

  const width = 96;
  const height = 28;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;

  const points = data
    .map((value, index) => {
      const x = (index / (data.length - 1)) * width;
      const y = height - ((value - min) / range) * (height - 4) - 2;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg width={width} height={height} className={styles.sparkline} aria-hidden="true">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function getTrendClass(tone: StatTileProps['trendTone'], isUp?: boolean): string {
  if (tone === 'success') return styles.trendSuccess;
  if (tone === 'warning') return styles.trendWarning;
  if (tone === 'danger') return styles.trendDanger;
  if (tone === 'neutral') return styles.trendNeutral;
  return isUp ? styles.trendUp : styles.trendDown;
}

const StatTile: React.FC<StatTileProps> = ({
  icon,
  label,
  value,
  subtitle,
  trend,
  trendTone = 'auto',
  spark,
  sparkColor = 'var(--brand-primary)',
  loading = false,
  title,
}) => {
  if (loading) {
    return (
      <div className={styles.tile} aria-hidden="true">
        <div className={`${styles.skeletonBlock} ${styles.skeletonLabel}`} />
        <div className={styles.body}>
          <div className={`${styles.skeletonBlock} ${styles.skeletonValue}`} />
          <div className={`${styles.skeletonBlock} ${styles.skeletonSpark}`} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tile} title={title}>
      <div className={styles.header}>
        <div className={styles.iconWrap}>{icon}</div>
        <span className={styles.label}>{label}</span>
      </div>
      <div className={styles.body}>
        <div className={styles.valueGroup}>
          <div className={styles.value}>{value}</div>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </div>
        <div className={styles.meta}>
          {trend?.value && trend.isUp !== null && trend.isUp !== undefined && (
            <span className={`${styles.trend} ${getTrendClass(trendTone, trend.isUp)}`}>
              {trend.isUp ? '↗' : '↘'} {trend.value}
            </span>
          )}
          {spark && spark.length > 0 && <Sparkline data={spark} color={sparkColor} />}
        </div>
      </div>
    </div>
  );
};

export default StatTile;
